/**
 * Client-Side Upload Prompt Modal
 * Handles direct file uploads via artifact endpoint for client-side sources
 */

import React, { useState } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { File, Clock, X, CheckCircle } from 'lucide-react';
import { uploadArtifact } from '../../api/artifacts';
import { useQueryClient } from '@tanstack/react-query';

interface ClientUploadPromptModalProps {
  projectId: string;
  sourceId: string;
  directoryHandle: FileSystemDirectoryHandle;
  uploadInfo: {
    files: Array<{ name: string; size: number; extension: string; path: string }>;
    totalSize: number;
  };
  selectedExtensions: string[];
  supportedExtensions: Set<string>;
  shouldIgnore: (path: string, name: string) => boolean;
  onClose: () => void;
}

export function ClientUploadPromptModal({
  projectId,
  sourceId,
  directoryHandle,
  uploadInfo,
  selectedExtensions,
  supportedExtensions,
  shouldIgnore,
  onClose,
}: ClientUploadPromptModalProps) {
  const queryClient = useQueryClient();
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };
  
  const handleUploadFiles = async () => {
    setUploadingFiles(true);
    setUploadProgress({});
    setUploadedCount(0);
    setUploadErrors([]);
    
    try {
      const filesToUpload: Array<{ file: File; path: string }> = [];
      
      const collectFiles = async (
        handle: FileSystemDirectoryHandle | FileSystemFileHandle,
        pathParts: string[] = []
      ) => {
        if ('kind' in handle && handle.kind === 'file') {
          const fileHandle = handle as FileSystemFileHandle;
          const file = await fileHandle.getFile();
          const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
          
          if (selectedExtensions.includes(ext) && supportedExtensions.has(ext)) {
            filesToUpload.push({ file, path: pathParts.join('/') });
          }
        } else if ('kind' in handle && handle.kind === 'directory') {
          const dirHandle = handle as FileSystemDirectoryHandle;
          const fullPath = pathParts.join('/');
          
          if (shouldIgnore(fullPath, dirHandle.name)) {
            return;
          }
          
          // @ts-ignore - FileSystemDirectoryHandle.entries() is valid but TypeScript types may be incomplete
          for await (const [name, entry] of dirHandle.entries()) {
            await collectFiles(entry, [...pathParts, name]);
          }
        }
      };
      
      await collectFiles(directoryHandle);
      
      console.log(`[CLIENT UPLOAD] Uploading ${filesToUpload.length} files...`);
      
      let successCount = 0;
      for (let i = 0; i < filesToUpload.length; i++) {
        const { file, path } = filesToUpload[i];
        try {
          setUploadProgress(prev => ({ ...prev, [path]: 0 }));
          
          await uploadArtifact(
            projectId,
            file,
            (progress) => {
              setUploadProgress(prev => ({ ...prev, [path]: progress }));
            }
          );
          
          successCount++;
          setUploadedCount(successCount);
          console.log(`[CLIENT UPLOAD] ✅ Uploaded: ${file.name} (${successCount}/${filesToUpload.length})`);
        } catch (err: any) {
          const errorMsg = `Failed to upload ${file.name}: ${err.message}`;
          console.error(`[CLIENT UPLOAD] ❌ ${errorMsg}`);
          setUploadErrors(prev => [...prev, errorMsg]);
        }
      }
      
      console.log(`[CLIENT UPLOAD] ✅ Completed: ${successCount}/${filesToUpload.length} files uploaded`);
      
      setTimeout(() => {
        setUploadingFiles(false);
        queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
        onClose();
      }, 2000);
      
    } catch (err: any) {
      console.error('[CLIENT UPLOAD] ❌ Error during upload:', err);
      setUploadErrors(prev => [...prev, `Upload failed: ${err.message}`]);
      setUploadingFiles(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload Files</h3>
            <button
              onClick={() => {
                if (!uploadingFiles) {
                  onClose();
                }
              }}
              className="text-gray-400 hover:text-gray-600"
              disabled={uploadingFiles}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              Your source has been registered. Now upload the selected files using the artifacts endpoint.
            </p>
          </div>
          
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Files to upload:</h4>
            <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
              <div className="divide-y divide-gray-200">
                {uploadInfo.files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <File className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate" title={file.path}>
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">{file.extension}</span>
                    </div>
                    <span className="text-xs text-gray-600 flex-shrink-0 ml-2">
                      {formatFileSize(file.size)}
                    </span>
                    {uploadingFiles && uploadProgress[file.path] !== undefined && (
                      <div className="ml-2 w-20">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all"
                            style={{ width: `${uploadProgress[file.path]}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center justify-between">
              <span className="font-semibold text-sm">Total:</span>
              <span className="font-semibold text-sm">
                {uploadInfo.files.length} files, {formatFileSize(uploadInfo.totalSize)}
              </span>
            </div>
          </div>
          
          {uploadingFiles && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <p className="text-sm font-semibold text-yellow-900">
                  Uploading: {uploadedCount} / {uploadInfo.files.length} files
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full transition-all"
                  style={{ width: `${(uploadedCount / uploadInfo.files.length) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          {uploadErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-900 mb-2">Upload Errors:</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {uploadErrors.map((error, idx) => (
                  <p key={idx} className="text-xs text-red-800">{error}</p>
                ))}
              </div>
            </div>
          )}
          
          {uploadedCount === uploadInfo.files.length && uploadedCount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-sm font-semibold text-green-900">
                  All files uploaded successfully!
                </p>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={() => {
                if (!uploadingFiles) {
                  onClose();
                }
              }}
              disabled={uploadingFiles}
              className="flex-1"
            >
              {uploadingFiles ? 'Uploading...' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleUploadFiles}
              disabled={uploadingFiles || uploadInfo.files.length === 0}
              className="flex-1"
            >
              {uploadingFiles 
                ? `Uploading... (${uploadedCount}/${uploadInfo.files.length})`
                : `Upload ${uploadInfo.files.length} File${uploadInfo.files.length !== 1 ? 's' : ''}`
              }
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

